import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  FilePlus2,
  Pencil,
  Send,
  Check,
  ClipboardCheck,
  FolderInput,
  CheckCheck,
  Ban,
  RotateCcw,
  Paperclip,
  Trash2,
  Clock,
  Undo2,
} from 'lucide-react'
import { submittalActivityVariant } from '@/lib/status-variants'

import * as Query from '../../../api/gql/query'

const DASH = '—'

// Same local date formatter SubmittalFieldsCard/SubmittalReviewsCard already
// use — kept as its own copy here rather than a shared util, matching this
// module's existing convention of duplicating the one-liner per card.
const formatDate = (t: (key: string, opts?: any) => string, value?: string | null) => {
  if (!value) return DASH
  return new Date(value).toLocaleDateString(t('date_locale') || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Mirrors SubmittalActivityAction (src/graphql/submittals/activity.ts). `Clock`
// is the fallback for any future action this map hasn't been taught yet —
// `action` is typed String on the backend, not a strict GraphQL enum.
const ACTION_ICON: Record<string, any> = {
  CREATED: FilePlus2,
  UPDATED: Pencil,
  SUBMITTED: Send,
  VERDICT: Check,
  CONSOLIDATED: ClipboardCheck,
  REVISION_CREATED: FilePlus2,
  PUBLISHED: FolderInput,
  CLOSED: CheckCheck,
  VOIDED: Ban,
  REOPENED: RotateCcw,
  WITHDRAWN: Undo2,
  ATTACHMENT_ADDED: Paperclip,
  ATTACHMENT_REMOVED: Trash2,
}

/**
 * Muted one-line summary under an activity row. Only UPDATED/VERDICT/
 * CONSOLIDATED and a REOPENED with its mode and reason carry meta worth
 * surfacing here — every other action's label already says everything.
 */
const getActivitySummary = (action: string, meta: any, t: (key: string, opts?: any) => string): string | null => {
  if (action === 'UPDATED') {
    const fields: string[] = Array.isArray(meta?.fields) ? meta.fields : []
    return fields.length ? fields.join(', ') : null
  }
  if (action === 'VERDICT' || action === 'CONSOLIDATED') {
    return meta?.verdict ? t(`Submittal_verdict_${meta.verdict}`) : null
  }
  if (action === 'REOPENED' && meta?.mode) {
    return [t(`Submittals_reopen_mode_${meta.mode}`), meta.reason].filter(Boolean).join(' — ')
  }
  return null
}

interface SubmittalActivityCardProps {
  submittalId: string
}

/**
 * Vertical timeline of a submittal's activity log (SUBMITTAL_ACTIVITY, newest
 * first per the backend's own `orderBy: { createdAt: 'desc' }`). Read-only —
 * this card never mutates anything, so it needs no loading spinners of its
 * own beyond the query's own loading state.
 */
const SubmittalActivityCard: React.FC<SubmittalActivityCardProps> = ({ submittalId }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery(Query.SUBMITTAL_ACTIVITY, {
    variables: { submittalId },
    skip: !submittalId,
  })

  const items: any[] = data?.submittalActivity || []

  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <CardTitle className="text-base">{t('Submittals_activity_title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">{t('Common_loading')}</div>
        ) : error ? (
          <div className="py-6 text-center text-sm text-muted-foreground">{t('Submittals_error_loading')}</div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">{t('Submittals_activity_empty')}</div>
        ) : (
          <div>
            {items.map((item, index) => {
              const Icon = ACTION_ICON[item.action] || Clock
              const variant = submittalActivityVariant(item.action)
              const summary = getActivitySummary(item.action, item.meta, t)
              const isLast = index === items.length - 1

              return (
                <div key={item.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <Badge
                      variant={variant}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full p-0"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </Badge>
                    {!isLast && <span className="w-px flex-1 bg-border" />}
                  </div>
                  <div className={cn('min-w-0 flex-1', !isLast && 'pb-4')}>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                      <span className="font-medium">{t(`Submittal_activity_${item.action}`)}</span>
                      <span className="text-muted-foreground">{item.user?.name || DASH}</span>
                      <span className="ml-auto shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                        {formatDate(t, item.createdAt)}
                      </span>
                    </div>
                    {summary && <p className="mb-0 mt-0.5 text-xs text-muted-foreground">{summary}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default SubmittalActivityCard
